import React, { useEffect, useMemo, useState } from 'react';
import Input from '../../../../shared/components/Input/Input';
import { ELEMENT_TYPES, PRIORITY_LABELS, STATUS_LABELS, SYNC_LABELS } from '../constants/planning.constants';
import { updatePlanningIntervalField } from '../utils/planningDates';
import { filterPlanningTasks } from '../planningReferences.api';
import { applyPlanningRelationChange } from '../utils/planningRelations';
import usePlanningDialog from '../hooks/usePlanningDialog';

const PlanningSelect = ({ label, name, value, onChange, disabled, children }) => (
  <label className="planning-field">
    <span>{label}</span>
    <select name={name} value={value ?? ''} onChange={onChange} disabled={disabled}>{children}</select>
  </label>
);

const validateDraft = (draft) => {
  const errors = {};
  if (!draft.title?.trim()) errors.title = 'Le titre est obligatoire.';
  if (!draft.startDate) errors.startDate = 'La date de début est obligatoire.';
  if (!draft.endDate) errors.endDate = 'La date de fin est obligatoire.';
  if (!draft.isAllDay && !draft.startTime) errors.startTime = 'L’heure de début est obligatoire.';
  if (!draft.isAllDay && !draft.endTime) errors.endTime = 'L’heure de fin est obligatoire.';
  if (draft.startDate && draft.endDate && `${draft.endDate}T${draft.endTime || '23:59'}` < `${draft.startDate}T${draft.startTime || '00:00'}`) errors.endDate = 'La fin doit être postérieure au début.';
  if (draft.durationMinutes < 0) errors.durationMinutes = 'La durée doit être positive.';
  if (draft.estimatedHours < 0) errors.estimatedHours = 'Le temps estimé doit être positif.';
  if (draft.completedHours < 0) errors.completedHours = 'Le temps réalisé doit être positif.';
  if (draft.progress < 0 || draft.progress > 100) errors.progress = 'L’avancement doit être compris entre 0 et 100.';
  return errors;
};

function InformationSection({ draft, errors, onChange, disabled }) {
  return (
    <section>
      <h3>Informations</h3>
      <Input fullWidth label="Titre" name="title" value={draft.title} onChange={onChange} required error={errors.title} disabled={disabled}/>
      <Input fullWidth multiline label="Description" name="description" value={draft.description} onChange={onChange} disabled={disabled}/>
      <div className="planning-form-grid">
        <PlanningSelect label="Type" name="elementType" value={draft.elementType} onChange={onChange} disabled={disabled}>{Object.entries(ELEMENT_TYPES).map(([key, value]) => <option key={key} value={key}>{value}</option>)}</PlanningSelect>
        <PlanningSelect label="Statut" name="status" value={draft.status} onChange={onChange} disabled={disabled}>{Object.entries(STATUS_LABELS).map(([key, value]) => <option key={key} value={key}>{value}</option>)}</PlanningSelect>
        <PlanningSelect label="Priorité" name="priority" value={draft.priority} onChange={onChange} disabled={disabled}>{Object.entries(PRIORITY_LABELS).map(([key, value]) => <option key={key} value={key}>{value}</option>)}</PlanningSelect>
      </div>
    </section>
  );
}

function ScheduleSection({ draft, errors, onChange, disabled }) {
  return (
    <section>
      <h3>Planification</h3>
      <label className="planning-check"><input type="checkbox" name="isAllDay" checked={draft.isAllDay} onChange={onChange} disabled={disabled}/> Journée entière</label>
      <div className="planning-form-grid">
        <Input label="Début" type="date" name="startDate" value={draft.startDate} onChange={onChange} required error={errors.startDate} disabled={disabled}/>
        {!draft.isAllDay && <Input label="Heure" type="time" name="startTime" value={draft.startTime} onChange={onChange} required error={errors.startTime} disabled={disabled}/>} 
        <Input label="Fin" type="date" name="endDate" value={draft.endDate} onChange={onChange} required error={errors.endDate} disabled={disabled}/>
        {!draft.isAllDay && <Input label="Heure de fin" type="time" name="endTime" value={draft.endTime} onChange={onChange} required error={errors.endTime} disabled={disabled}/>} 
        <Input label="Durée (minutes)" type="number" min="0" name="durationMinutes" value={draft.durationMinutes} onChange={onChange} error={errors.durationMinutes} disabled={disabled}/>
      </div>
    </section>
  );
}

function AssignmentSection({ draft, resources, references, onChange, disabled }) {
  const tasks = filterPlanningTasks(references.tasks, draft);
  return (
    <section>
      <h3>Affectation</h3>
      <PlanningSelect label="Collaborateur" name="resourceId" value={draft.resourceId} onChange={onChange} disabled={disabled}>
        <option value="">Non affecté</option>
        {resources.map((resource) => <option key={resource.id} value={resource.id}>{resource.name}</option>)}
      </PlanningSelect>
      {references.error && <p className="planning-error">{references.error}</p>}
      <div className="planning-form-grid">
        <PlanningSelect label="Projet / Intervention" name="projectId" value={draft.projectId} onChange={onChange} disabled={disabled || references.loading}><option value="">Aucune réalisation</option>{references.realizations.map((row) => <option key={row.id} value={row.id}>{row.displayLabel}{row.status === 'archived' ? ' — Archivé' : ''}</option>)}</PlanningSelect>
        <PlanningSelect label="Tâche" name="taskId" value={draft.taskId} onChange={onChange} disabled={disabled || !draft.projectId}><option value="">Aucune tâche</option>{tasks.map((row) => <option key={row.id} value={row.id}>{row.displayLabel}</option>)}</PlanningSelect>
        <PlanningSelect label="Livrable" name="deliverableId" value={draft.deliverableId} onChange={onChange} disabled={disabled || !draft.projectId || references.deliverablesLoading}><option value="">Aucun livrable</option>{references.deliverables.map((row) => <option key={row.id} value={row.id}>{row.displayLabel}</option>)}</PlanningSelect>
        <PlanningSelect label="Client" name="clientId" value={draft.clientId} onChange={onChange} disabled={disabled || references.loading}><option value="">Aucun client</option>{references.clients.map((row) => <option key={row.id} value={row.id}>{row.displayLabel}</option>)}</PlanningSelect>
      </div>
    </section>
  );
}

function WorkloadSection({ draft, errors, remainingHours, onChange, disabled }) {
  return (
    <section>
      <h3>Charge</h3>
      <div className="planning-form-grid">
        <Input label="Estimé (h)" type="number" min="0" name="estimatedHours" value={draft.estimatedHours} onChange={onChange} error={errors.estimatedHours} disabled={disabled}/>
        <Input label="Réalisé (h)" type="number" min="0" name="completedHours" value={draft.completedHours} onChange={onChange} error={errors.completedHours} disabled={disabled}/>
        <Input label="Temps restant (h)" type="number" value={remainingHours} readOnly/>
        <Input label="Avancement (%)" type="number" min="0" max="100" name="progress" value={draft.progress} onChange={onChange} error={errors.progress} disabled={disabled}/>
      </div>
      <label className="planning-check"><input type="checkbox" name="isBillable" checked={draft.isBillable} onChange={onChange} disabled={disabled}/> Facturable</label>
    </section>
  );
}

function SyncSection({ draft, dirty, onSync }) {
  return (
    <details>
      <summary>Synchronisation avancée</summary>
      <p>État : {SYNC_LABELS[draft.syncStatus]}</p>
      <Input fullWidth readOnly label="Identifiant externe CalDAV" value={draft.externalEventId || ''}/>
      {draft.lastSyncedAt && <p>Dernière synchronisation : {new Date(draft.lastSyncedAt).toLocaleString('fr-FR')}</p>}
      {draft.syncError && <p className="planning-error">{draft.syncError}</p>}
      {dirty && <p>Enregistrez vos modifications avant de synchroniser cet élément.</p>}
      <button className="basic-button" disabled={!draft.id || dirty} onClick={() => onSync(draft.id)}>Synchroniser cet élément</button>
    </details>
  );
}

export default function PlanningEventDrawer({ event, resources, references, onClose, onSave, onDelete, onSync, saving, apiError }) {
  const [draft, setDraft] = useState(event);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState({});
  const close = () => { if (!dirty || window.confirm('Abandonner les modifications non enregistrées ?')) onClose(); };
  const dialogRef = usePlanningDialog(close, Boolean(draft));
  useEffect(() => { setDraft(event); setDirty(false); setErrors({}); }, [event]);
  const remainingHours = useMemo(() => Math.max(0, Number(draft?.estimatedHours || 0) - Number(draft?.completedHours || 0)), [draft?.estimatedHours, draft?.completedHours]);
  if (!draft) return null;
  const disabled = Boolean(draft.isLocked);
  const change = ({ target }) => {
    const value = target.type === 'checkbox'
      ? target.checked
      : target.type === 'number'
        ? target.value === '' ? '' : Number(target.value)
        : target.value;
    setDraft((current) => {
      if (target.name === 'projectId') return applyPlanningRelationChange(current, target.name, value, references);
      return updatePlanningIntervalField(current, target.name, value);
    });
    if (target.name === 'projectId') references.loadDeliverables(value);
    setDirty(true);
    setErrors((current) => ({ ...current, [target.name]: undefined }));
  };
  const submit = () => { const nextErrors = validateDraft(draft); setErrors(nextErrors); if (!Object.keys(nextErrors).length) onSave({ ...draft, remainingHours }); };
  return (
    <div className="planning-drawer-backdrop" onMouseDown={(eventTarget) => eventTarget.target === eventTarget.currentTarget && close()}>
      <aside ref={dialogRef} className="planning-drawer" role="dialog" aria-modal="true" aria-label="Édition d’un élément" tabIndex="-1">
        <header>
          <div><strong>{draft.id ? 'Modifier l’élément' : 'Nouvel élément'}</strong>{dirty && <span className="planning-dirty">Modifié</span>}{disabled && <span className="planning-locked">Verrouillé — consultation uniquement</span>}</div>
          <button aria-label="Fermer" onClick={close}>×</button>
        </header>
        <div className="planning-drawer__body">
          {Object.keys(errors).length > 0 && <div className="planning-notice planning-notice--error" role="alert">Veuillez corriger les champs indiqués.</div>}
          {apiError && <div className="planning-notice planning-notice--error" role="alert">{apiError}</div>}
          <InformationSection draft={draft} errors={errors} onChange={change} disabled={disabled}/>
          <ScheduleSection draft={draft} errors={errors} onChange={change} disabled={disabled}/>
          <AssignmentSection draft={draft} resources={resources} references={references} onChange={change} disabled={disabled}/>
          <WorkloadSection draft={draft} errors={errors} remainingHours={remainingHours} onChange={change} disabled={disabled}/>
          <section><h3>Localisation</h3><Input fullWidth label="Lieu / lien de visioconférence" name="location" value={draft.location} onChange={change} disabled={disabled}/></section>
          <SyncSection draft={draft} dirty={dirty} onSync={onSync}/>
        </div>
        <footer>
          {draft.id && <button className="basic-button basic-button--danger" title={dirty ? 'Supprime l’élément enregistré, sans tenir compte du brouillon.' : undefined} onClick={() => onDelete(draft)}>Supprimer l’élément enregistré</button>}
          <span/>
          <button className="basic-button" onClick={close}>Annuler</button>
          <button className="basic-button active" disabled={saving || !dirty || disabled} onClick={submit}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
        </footer>
      </aside>
    </div>
  );
}
